import log from 'loglevel';
import {
  AvatarFromSmplReqPayload,
  AvatarFromStarReqPayload,
  CredentialsResPayload,
  GarmentReqPayload,
  MessageIncoming,
  MessageIncomingType,
  MessageOutgoing,
  MessageType,
} from './iframeProtocol';

const DISCARDED_MESSAGES = ['webpackClose', 'webpackInvalid', 'webpackOk'];

export const isEmbedded = () => window.self !== window.top;

type AvatarFromSmplHandler = (payload: AvatarFromSmplReqPayload) => void;
type AvatarFromStarHandler = (payload: AvatarFromStarReqPayload) => void;
type CredentialsHandler = (payload: CredentialsResPayload) => void;
type GarmentHandler = (payload: GarmentReqPayload) => void;
type GarmentFromSkuHandler = (payload: string) => void;
type SessionHandler = (payload: string) => void;

type MessageHandler = (event: MessageEvent<any>) => void;

// Avatar from SMPL listeners
const avatarFromSmplListeners: AvatarFromSmplHandler[] = [];
export function addAvatarFromSmplListener(listener: AvatarFromSmplHandler) {
  if (!avatarFromSmplListeners.includes(listener)) {
    avatarFromSmplListeners.push(listener);
  }
}
export function removeAvatarFromSmplListener(listener: AvatarFromSmplHandler) {
  const index = avatarFromSmplListeners.findIndex(value => value === listener);
  if (index !== -1) {
    avatarFromSmplListeners.splice(index, 1);
  }
}

// Avatar from STAR listeners
const avatarFromStarListeners: AvatarFromStarHandler[] = [];
export function addAvatarFromStarListener(listener: AvatarFromStarHandler) {
  if (!avatarFromStarListeners.includes(listener)) {
    avatarFromStarListeners.push(listener);
  }
}
export function removeAvatarFromStarListener(listener: AvatarFromStarHandler) {
  const index = avatarFromStarListeners.findIndex(value => value === listener);
  if (index !== -1) {
    avatarFromStarListeners.splice(index, 1);
  }
}

// Credentials listeners
const credentialsListeners: CredentialsHandler[] = [];
export function addCredentialsListener(listener: CredentialsHandler) {
  if (!credentialsListeners.includes(listener)) {
    credentialsListeners.push(listener);
  }
}
export function removeCredentialsListener(listener: CredentialsHandler) {
  const index = credentialsListeners.findIndex(value => value === listener);
  if (index !== -1) {
    credentialsListeners.splice(index, 1);
  }
}

// Garment listeners
const garmentListeners: GarmentHandler[] = [];
export function addGarmentListener(listener: GarmentHandler) {
  if (!garmentListeners.includes(listener)) {
    garmentListeners.push(listener);
  }
}
export function removeGarmentListener(listener: GarmentHandler) {
  const index = garmentListeners.findIndex(value => value === listener);
  if (index !== -1) {
    garmentListeners.splice(index, 1);
  }
}

// Garment from SKU listeners
const garmentFromSkuListeners: GarmentFromSkuHandler[] = [];
export function addGarmentFromSkuListener(listener: GarmentFromSkuHandler) {
  if (!garmentFromSkuListeners.includes(listener)) {
    garmentFromSkuListeners.push(listener);
  }
}
export function removeGarmentFromSkuListener(listener: GarmentFromSkuHandler) {
  const index = garmentFromSkuListeners.findIndex(value => value === listener);
  if (index !== -1) {
    garmentFromSkuListeners.splice(index, 1);
  }
}

// Session listeners
const sessionListeners: SessionHandler[] = [];
export function addSessionListener(listener: SessionHandler) {
  if (!sessionListeners.includes(listener)) {
    sessionListeners.push(listener);
  }
}
export function removeSessionListener(listener: SessionHandler) {
  const index = sessionListeners.findIndex(value => value === listener);
  if (index !== -1) {
    sessionListeners.splice(index, 1);
  }
}

// Internal message listeners
function credentialsListener(event: MessageEvent<any>) {
  const payload = event.data.payload as CredentialsResPayload;
  // protocol validation
  // NOTE: evaluate using class-validator, zod?
  if (!payload.appKey) {
    log.error(`[Fitroom] appKey must be a string`);
    return;
  }
  // emit to listeners
  for (const listener of credentialsListeners) {
    listener(payload);
  }
}

function avatarFromSmplListener(event: MessageEvent<any>) {
  const payload = event.data.payload as AvatarFromSmplReqPayload;
  // protocol validation
  if (!payload.sex) {
    log.error(`[Fitroom] sex must be a string`);
    return;
  }
  // emit to listeners
  for (const listener of avatarFromSmplListeners) {
    listener(payload);
  }
}

function avatarFromStarListener(event: MessageEvent<any>) {
  const payload = event.data.payload as AvatarFromStarReqPayload;
  // protocol validation
  if (!payload.sex) {
    log.error(`[Fitroom] sex must be a string`);
    return;
  }

  // emit to listeners
  for (const listener of avatarFromStarListeners) {
    listener(payload);
  }
}

function garmentListener(event: MessageEvent<any>) {
  const payload = event.data.payload as GarmentReqPayload;
  // protocol validation
  if (!payload.id) {
    log.error(`[Fitroom] ID must be a UUID`);
    return;
  }

  // emit to listeners
  for (const listener of garmentListeners) {
    listener(payload);
  }
}

function garmentFromSkuListener(event: MessageEvent<any>) {
  const payload = event.data.payload as string;
  // protocol validation
  if (!payload) {
    log.error(`[Fitroom] SKU must be a string`);
    return;
  }

  // emit to listeners
  for (const listener of garmentFromSkuListeners) {
    listener(payload);
  }
}

function sessionListener(event: MessageEvent<any>) {
  const payload = event.data.payload as string;
  // protocol validation
  if (!payload) {
    log.error(`[Fitroom] Session must be a string`);
    return;
  }

  // emit to listeners
  for (const listener of sessionListeners) {
    listener(payload);
  }
}

/**
 * Send message to host.
 * @param msg Outgoing message.
 */
export function sendMessage(msg: MessageOutgoing) {
  if (!window.top) {
    return;
  }

  window.top.postMessage(msg, '*');
  log.debug(`[Fitroom] Sent message ${msg.type}`);
}

const listeners: Record<MessageIncomingType, MessageHandler> = {
  [MessageType.AVATAR_FROM_SMPL_REQ]: avatarFromSmplListener,
  [MessageType.AVATAR_FROM_STAR_REQ]: avatarFromStarListener,
  [MessageType.CREDENTIALS_RES]: credentialsListener,
  [MessageType.GARMENT_REQ]: garmentListener,
  [MessageType.GARMENT_SKU_REQ]: garmentFromSkuListener,
  [MessageType.SESSION_REQ]: sessionListener,
};

function messageListener(event: MessageEvent<any>) {
  const message = event.data as MessageIncoming;

  // Bad format message.
  if (!message.type) {
    return;
  }
  // Discarded message.
  if (DISCARDED_MESSAGES.includes(message.type)) {
    return;
  }

  const type = message.type;
  log.debug(`[Fitroom] Received message ${type}`);

  if (listeners[type]) {
    return listeners[type](event);
  }

  log.warn(`[Fitroom] Unrecognized message ${type}`);
}

/**
 * Client start.
 */
export const iframeClientStart = () => {
  if (!isEmbedded()) {
    return;
  }

  // Widget referrer.
  if (document.referrer) {
    log.info(`[Fitroom] Initializing widget with referrer ${document.referrer}`);
  }

  // Attach listeners.
  window.addEventListener('message', messageListener);

  // Request credentials.
  sendMessage({ type: MessageType.CREDENTIALS_REQ });
};
